import { parseImageAssetId } from '@sanity/asset-utils';
import { NextRouter } from 'next/router';
import type { CustomContent } from 'processes/DynamicLanders/types';
import { PortableTextData } from 'schema/studio/common/portableText';
import { isPreviewRouteView } from 'shared/utils/previewRouteHelpers';
import { urlFor } from 'shared/utils/urlFor';
import {
  CONTENT_BACKGROUND_COLORS,
  HEADLINE_SIZES,
  IMAGE_POSITIONS,
  ITEM_FLIP_STATES,
  PADDING_SIZES,
  PREVIEW_PLACEHOLDER_QUERY_PARAM
} from './constants';
import {
  ContentBackgroundColor,
  ContentBlockFieldOptions,
  ContentHeadlineSize,
  ContentPaddingSize,
  ImagePosition,
  ItemFlipState,
  ResponsiveContentBlockImageType
} from './types';

export const shouldShowContentBlockPlaceholders = (router: NextRouter) => {
  // Never show placeholders if we're not in a preview route.
  if (!isPreviewRouteView(router)) {
    return false;
  }

  // Check our query param to see if we should show placeholders.
  const param = router.query[PREVIEW_PLACEHOLDER_QUERY_PARAM];
  if (param !== undefined) {
    return `${param}` === 'true' || `${param}` === '1';
  }

  return true;
};

export const isContentBackgroundColor = (color: string | null): color is ContentBackgroundColor =>
  CONTENT_BACKGROUND_COLORS.includes(color as ContentBackgroundColor);

export const isContentPaddingSize = (size: string | null): size is ContentPaddingSize =>
  PADDING_SIZES.includes(size as ContentPaddingSize);

export const isContentHeadlineSize = (size: string | null): size is ContentHeadlineSize =>
  HEADLINE_SIZES.includes(size as ContentHeadlineSize);

export const isImagePosition = (position: string | null): position is ImagePosition =>
  IMAGE_POSITIONS.includes(position as ImagePosition);

const isItemFlipState = (state: string | null): state is ItemFlipState =>
  ITEM_FLIP_STATES.includes(state as ItemFlipState);

export const parseItemFlipState = (content: string | null): ItemFlipState =>
  isItemFlipState(content) ? content : 'default';

export const parseContentBlockToggle = (i: string) => i === '1' || i === 'true';

export const getComplexContentObjectArray = <T extends Record<string, any>>(
  content: string | null | undefined
): Partial<T>[] | null => {
  if (typeof content === 'string' && content.startsWith('[') && content.endsWith(']')) {
    return JSON.parse(content);
  }
  if (Array.isArray(content)) {
    return content;
  }
  return null;
};

export const getComplexContentObject = <T extends Record<string, any>>(
  content: any
): (T extends any[] ? never : Partial<T>) | null => {
  if (typeof content === 'string' && content.startsWith('{') && content.endsWith('}')) {
    return JSON.parse(content);
  }
  if (typeof content === 'object' && !Array.isArray(content)) {
    return content;
  }
  return null;
};

export const getSanityImageFromString = (ref: string) => {
  if (!ref) {
    return null;
  }

  const { url } = urlFor({ asset: { _ref: ref, _type: 'reference' } });
  const { width, height } = parseImageAssetId(ref);

  return {
    src: url,
    width,
    height
  };
};

export const getResponsiveSanityImage = (
  image: ResponsiveContentBlockImageType | null | undefined
) => {
  const result: {
    [Key in keyof NonNullable<typeof image>]?: ReturnType<typeof getSanityImageFromString>;
  } = {};

  if (!image) {
    return result;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const [key, rawSrc] of Object.entries(image)) {
    if (rawSrc) {
      const data = getSanityImageFromString(rawSrc);
      if (data) {
        result[key as keyof typeof image] = data;
      }
    }
  }
  return result;
};

export function getPlaceholderForPreview<T>(router: NextRouter, placeholder: T, fallback: T): T;
export function getPlaceholderForPreview<T>(
  router: NextRouter,
  placeholder: T,
  fallback?: null
): T | typeof fallback;
export function getPlaceholderForPreview<T = null>(
  router: NextRouter,
  placeholder: T,
  fallback: T | null = null
) {
  if (shouldShowContentBlockPlaceholders(router)) {
    return placeholder;
  }
  return fallback;
}

export const getContentBlockField = (
  customContent: CustomContent[],
  type: string,
  { slug, router, previewPlaceholder }: ContentBlockFieldOptions
) => {
  const content = customContent.find((cc) => {
    if (cc.content_type !== type) {
      return false;
    }
    if (slug) {
      return cc.section_slug === slug;
    }
    return true;
  })?.content;

  if (router && previewPlaceholder) {
    return content || getPlaceholderForPreview(router, previewPlaceholder, '');
  }
  return content || null;
};

export const makePortableTextBlock = (text: string) =>
  [
    {
      children: [
        {
          _type: 'span',
          _key: text,
          text
        }
      ],
      _type: 'block' as const,
      style: 'normal'
    }
  ] satisfies PortableTextData;
